import { App, Button, Switch, Tooltip } from "antd";
import { ContactError } from "./ContactError";
import { EditOutlined, FileExclamationOutlined, FileOutlined } from "@ant-design/icons";
import { RequestChannel } from "@vyne-shared/api/VynePayments/responses";
import { captureException } from "@sentry/react";
import { getContactValidity } from "./contactErrorHelpers";
import { showBulkRequestEditModal } from "../showBulkRequestEditModal";
import { showTakePaymentModal } from "../../../NewPaymentModal/showTakePaymentModal";
import { useAllowComm } from "api/clients/useAllowComm";
import { validateEmail } from "@vyne-shared/utils/email";
import { validatePhoneNational } from "../bulkRequestUtils.phone";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { SaveCallback } from "../useChangeOverlay";

interface RecordComponentProps {
    readonly record: BulkRequestRecord;
}

export const PatientName = ({ record }: RecordComponentProps) => (
    <div className="bulk-request-patient-name">
        <div>
            {record.fullName}
            <br />
            <time dateTime={record.dateOfBirth}>{record.dateOfBirth}</time>
        </div>

        <span>
            {record.hasOutstandingClaims && (
                <Tooltip placement="top" title="Patient has outstanding claim(s)">
                    <FileExclamationOutlined className="outstanding-claim-icon" />
                </Tooltip>
            )}
            {record.hasActivePaymentPlan && (
                <Tooltip placement="top" title="Patient has active payment plan">
                    <FileOutlined className="payment-plan-icon" />
                </Tooltip>
            )}
        </span>
    </div>
);

export const PatientPhone = ({ record }: RecordComponentProps) => {
    // Parse phone number using same library used by react-phone-number-input control
    const phone = validatePhoneNational(record);

    const contactValidity = getContactValidity(RequestChannel.SMS, record.requestChannel, record.phone, !!phone);

    switch (contactValidity) {
        case "ok":
            return (
                <Tooltip placement="topLeft" title={phone}>
                    {phone}
                </Tooltip>
            );
        case "missing-ok":
            return <i>No phone available</i>;
        default:
            return (
                <ContactError
                    contactValidity={contactValidity}
                    warningMessage={`Invalid phone "${record.phone}" won't be used.`}
                    errorMessage={`"${record.phone}" is not a valid North American phone number.`}
                    missingErrorMessage="Phone number is required for SMS messaging."
                    type="phone"
                />
            );
    }
};

export const PatientEmail = ({ record }: RecordComponentProps) => {
    const contactValidity = getContactValidity(
        RequestChannel.Email,
        record.requestChannel,
        record.email,
        validateEmail(record.email),
    );

    switch (contactValidity) {
        case "ok":
            return (
                <Tooltip placement="topLeft" title={record.email}>
                    {record.email}
                </Tooltip>
            );
        case "missing-ok":
            return <i>No email available</i>;
        default:
            return (
                <ContactError
                    contactValidity={contactValidity}
                    warningMessage={`Invalid email address "${record.email}" won't be used.`}
                    errorMessage={`"${record.email}" is not a valid email address.`}
                    missingErrorMessage="Email address is required to send email."
                    type="email"
                />
            );
    }
};

export const PatientAllowCommunication = ({ record }: RecordComponentProps) => {
    const { message } = App.useApp();

    const { mutate, isPending } = useAllowComm(record.pmsId, {
        onError: (error, checked) => {
            // record and display any error
            const msg = `Couldn't ${checked ? "enable" : "disable"} communication for patient ${record.fullName}`;
            captureException(new Error(msg, { cause: error }));

            message.warning(msg, 10);
        },
    });

    return (
        <Switch
            checked={record.allowCommunication}
            loading={isPending}
            onChange={(checked) => mutate(checked)}
            data-testid="PatientAllowCommunication"
            aria-label="Allow Contact"
        />
    );
};

interface EditRecordComponentProps extends RecordComponentProps {
    save: SaveCallback;
}

export const PatientActions = ({ record, save }: EditRecordComponentProps) => (
    <div className="user-actions bulk-request-actions">
        <Button
            type="link"
            title="Take a Payment"
            disabled={!record.allowCommunication}
            onClick={() => void showTakePaymentModal(record.pmsId)}
        >
            Pay Now
        </Button>
        <Button
            type="link"
            className="bulk-request-actions-edit"
            title="Edit"
            disabled={!record.allowCommunication}
            onClick={() => void showBulkRequestEditModal(record, save)}
        >
            <EditOutlined title="Edit" />
        </Button>
    </div>
);
